import { Injectable } from '@angular/core';
import { ToastService } from '@core/components/toasts/toasts.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilderService } from 'services/form-builder/form-builder.service';
import { guidEmpty, isGuid } from 'utils/helps';
import {
  ConfirmDialogComponent,
  ConfirmDialogEnum,
} from 'app/layout/components/confirm-dialog/confirm-dialog.component';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export type FormBuilderElementType = {
  itemId: string;
  groupId: string;
  optionId: string;
};

@Injectable({ providedIn: 'root' })
export class FormBuilderElementService {
  public isRemoving: boolean = false;

  constructor(
    private modal: NgbModal,
    private formService: FormBuilderService,
    private toast: ToastService
  ) { }

  // Atualização de elemento
  public update(payload: FormBuilderElementType): Observable<boolean> {
    return this.formService.getAnswersCount(payload).pipe(
      switchMap(res => {
        if (res === 0) {
          this.toast.showSuccess('Item atualizado com sucesso.', 'top-center');
          return of(true);
        }

        return from(this.confirmUpdate(res)).pipe(
          map(confirmed => {
            if (confirmed) {
              this.toast.showSuccess('Item atualizado com sucesso.', 'top-center');
              return true;
            } else {
              this.toast.showInfo('Atualização cancelada pelo usuário.', 'top-center');
              return false;
            }
          })
        );
      }),
      catchError(() => {
        this.toast.showError('Erro ao verificar respostas associadas.', 'top-center');
        return of(false);
      })
    );
  }

  // Remoção de elemento
  public remove(
    payload: FormBuilderElementType,
    onSuccess: () => void,
    successMessage: string,
    errorMessage: string
  ): void {
    if (this.isAllEmptyOrInvalid(payload)) {
      this.handleSuccess(onSuccess, successMessage);
      return;
    }

    this.isRemoving = true; // ativa overlay imediatamente

    this.formService.getAnswersCount(payload).subscribe({
      next: res => {
        if (res === 0) {
          this.executeRemoval(payload, onSuccess, successMessage, errorMessage);
        } else {
          this.confirmRemoval(res)
            .then(confirmed => {
              if (confirmed) {
                this.executeRemoval(payload, onSuccess, successMessage, errorMessage);
              } else {
                this.isRemoving = false; // cancela overlay se usuário não confirmar
              }
            })
            .catch(() => {
              this.isRemoving = false; // cancela overlay se modal for fechado
            });
        }
      },
      error: () => {
        this.toast.showError(errorMessage, 'top-center');
        this.isRemoving = false;
      },
    });
  }

  private isAllEmptyOrInvalid({ itemId, groupId, optionId }: any): boolean {
    const empty = guidEmpty;
    const invalid = (id: string) => !isGuid(id) || id === empty;
    return invalid(itemId) && invalid(groupId) && invalid(optionId);
  }

  private handleSuccess(onSuccess: () => void, message: string): void {
    onSuccess();
    this.isRemoving = false;
    this.toast.showSuccess(message, 'top-center');
  }

  private executeRemoval(
    payload: any,
    onSuccess: () => void,
    successMessage: string,
    errorMessage: string
  ): void {
    this.isRemoving = true; // garante overlay durante remoção
    this.formService.removeFormElement(payload).subscribe({
      next: () => this.handleSuccess(onSuccess, successMessage),
      error: () => {
        this.toast.showError(errorMessage, 'top-center');
        this.isRemoving = false;
      },
    });
  }

  private async confirmRemoval(answerCount: number): Promise<boolean> {
    const modalRef = this.modal.open(ConfirmDialogComponent, {
      animation: false,
      centered: true,
    });

    Object.assign(modalRef.componentInstance, {
      type: ConfirmDialogEnum.SWITCH_ALERT,
      title: 'Confirmar remoção',
      messageConfirm: 'Sim, remover',
      messageCancel: 'Cancelar',
      icon: 'info',
      message: `Ao remover este elemento, <b>${answerCount}</b> resposta(s) associada(s) serão afetadas. Deseja continuar?`,
    });

    return modalRef.result.catch(() => false);
  }

  private async confirmUpdate(answerCount: number): Promise<boolean> {
    const modalRef = this.modal.open(ConfirmDialogComponent, {
      animation: false,
      centered: true,
    });

    Object.assign(modalRef.componentInstance, {
      type: ConfirmDialogEnum.SWITCH_ALERT,
      title: 'Confirmar atualização',
      messageConfirm: 'Sim, atualizar',
      messageCancel: 'Cancelar',
      icon: 'info',
      message: `Ao atualizar este elemento, <b>${answerCount}</b> resposta(s) associada(s) serão afetadas. Deseja continuar?`,
    });

    return modalRef.result.catch(() => false);
  }
}
